import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Calendar, Users, Wrench, Star, Mail, MessageCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardAnalytics() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { title: lang === 'en' ? 'Total Bookings' : 'Prenotazioni Totali', value: stats.total_bookings, icon: Calendar, color: 'from-blue-500 to-blue-600' },
    { title: lang === 'en' ? 'Pending Bookings' : 'Prenotazioni Pendenti', value: stats.pending_bookings, icon: Calendar, color: 'from-yellow-500 to-yellow-600' },
    { title: lang === 'en' ? 'Total Customers' : 'Clienti Totali', value: stats.total_customers, icon: Users, color: 'from-green-500 to-green-600' },
    { title: lang === 'en' ? 'Active Services' : 'Servizi Attivi', value: stats.total_services, icon: Wrench, color: 'from-purple-500 to-purple-600' },
    { title: lang === 'en' ? 'Average Rating' : 'Rating Medio', value: stats.avg_rating.toFixed(1), icon: Star, color: 'from-orange-500 to-orange-600' },
    { title: lang === 'en' ? 'Unread Messages' : 'Messaggi Non Letti', value: stats.unread_messages, icon: Mail, color: 'from-red-500 to-red-600' },
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-analytics">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              {lang === 'en' ? 'Analytics & Statistics' : 'Analytics & Statistiche'}
            </h1>
            <p className="text-gray-600">{lang === 'en' ? 'Workshop performance overview' : 'Panoramica delle performance dell\'officina'}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="analytics-loading" />
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {statCards.map((card, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow" data-testid={`stat-card-${idx}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center shadow-lg`}>
                        <card.icon className="w-6 h-6 text-white" />
                      </div>
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{card.value}</div>
                    <div className="text-gray-600">{card.title}</div>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
                    {lang === 'en' ? 'Quick Actions' : 'Azioni Rapide'}
                  </h2>
                  <div className="space-y-3">
                    <a href="/dashboard/copilot" className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                      <MessageCircle className="w-5 h-5 text-[#0066b2]" />
                      <span className="font-medium text-gray-900">{lang === 'en' ? 'Ask AI Co-Pilot for insights' : 'Chiedi insights all\'AI Co-Pilot'}</span>
                    </a>
                    <a href="/dashboard/bookings" className="flex items-center gap-3 p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                      <Calendar className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-gray-900">{lang === 'en' ? 'Manage today\'s bookings' : 'Gestisci prenotazioni di oggi'}</span>
                    </a>
                    <a href="/dashboard/messages" className="flex items-center gap-3 p-3 bg-red-50 rounded-xl hover:bg-red-100 transition-colors">
                      <Mail className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-gray-900">{lang === 'en' ? `Read unread messages (${stats?.unread_messages || 0})` : `Leggi messaggi non letti (${stats?.unread_messages || 0})`}</span>
                    </a>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
                    {lang === 'en' ? 'Info' : 'Informazioni'}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    {lang === 'en'
                      ? 'Statistics are updated in real-time. Monitor performance constantly to optimize the services offered. Use the AI Co-Pilot for advanced analysis and marketing suggestions.'
                      : 'Le statistiche vengono aggiornate in tempo reale. Monitora costantemente le performance per ottimizzare i servizi offerti. Usa l\'AI Co-Pilot per analisi avanzate e suggerimenti marketing.'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
